import { AlertTriangle, Edit3, Power, PowerOff, Tags } from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import { RestaurantRawMaterialDTO } from '../../../../types/restaurant/RestaurantRawMaterialTypes';

interface RawMaterialCardProps {
  material: RestaurantRawMaterialDTO;
  onIdentifiers: (material: RestaurantRawMaterialDTO) => void;
  onEdit: (material: RestaurantRawMaterialDTO) => void;
  onToggleActive: (material: RestaurantRawMaterialDTO) => void;
}

const formatStock = (value: number | null, unitCode: string) =>
  `${Number(value ?? 0).toLocaleString('es-SV', {
    maximumFractionDigits: 6,
  })} ${unitCode || ''}`.trim();

const formatMoney = (value: number | null) =>
  new Intl.NumberFormat('es-SV', {
    style: 'currency',
    currency: 'USD',
  }).format(value ?? 0);

const RawMaterialCard = ({
  material,
  onIdentifiers,
  onEdit,
  onToggleActive,
}: RawMaterialCardProps) => {
  const showLowStock = material.active && material.lowStock;

  return (
    <article
      className={`flex h-full flex-col rounded-xl border p-5 shadow-card-2 transition-shadow duration-200 hover:shadow-card dark:shadow-none ${
        showLowStock
          ? 'border-warning/50 bg-warning/5 dark:border-warning/40 dark:bg-warning/5'
          : material.active
          ? 'border-stroke bg-white dark:border-strokedark dark:bg-boxdark'
          : 'border-stroke bg-gray-2/60 dark:border-strokedark dark:bg-boxdark-2/60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wider text-primary">
            {material.categoryName} · {material.code}
          </p>
          <h3 className="mt-1 truncate font-semibold text-black dark:text-white">
            {material.name}
          </h3>
        </div>

        {showLowStock ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning/15 px-2.5 py-1 text-xs font-medium text-meta-8 dark:text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            Stock bajo
          </span>
        ) : (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
              material.active
                ? 'bg-success/10 text-success'
                : 'bg-danger/10 text-danger'
            }`}
          >
            {material.active ? 'Disponible' : 'Inactiva'}
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-stroke/70 bg-gray-2 p-3 dark:border-strokedark dark:bg-meta-4">
          <p className="text-xs text-bodydark2">Stock actual</p>
          <p
            className={`mt-1 font-semibold ${
              showLowStock ? 'text-meta-8 dark:text-warning' : 'text-black dark:text-white'
            }`}
          >
            {formatStock(material.currentStock, material.unitCode)}
          </p>
        </div>

        <div className="rounded-lg border border-stroke/70 bg-gray-2 p-3 dark:border-strokedark dark:bg-meta-4">
          <p className="text-xs text-bodydark2">Stock mínimo</p>
          <p className="mt-1 font-semibold text-black dark:text-white">
            {formatStock(material.minimumStock, material.unitCode)}
          </p>
        </div>
      </div>

      <div className="mt-4 flex-1 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-bodydark2">Costo unitario</span>
          <strong className="text-black dark:text-white">
            {formatMoney(material.cost)}
          </strong>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="shrink-0 text-bodydark2">Proveedor</span>
          <span
            className="truncate text-right text-black dark:text-white"
            title={material.supplierName || 'Sin proveedor'}
          >
            {material.supplierName || 'Sin proveedor'}
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-stroke pt-4 dark:border-strokedark">
        <ActionButton
          label="Identificadores"
          icon={<Tags className="h-4 w-4" />}
          variant="secondary"
          size="sm"
          onClick={() => onIdentifiers(material)}
          className="col-span-2 min-h-[38px]"
        />

        <ActionButton
          label="Editar"
          icon={<Edit3 className="h-4 w-4" />}
          variant="primary"
          size="sm"
          onClick={() => onEdit(material)}
          className="min-h-[38px]"
        />

        <ActionButton
          label={material.active ? 'Desactivar' : 'Reactivar'}
          icon={
            material.active ? (
              <PowerOff className="h-4 w-4" />
            ) : (
              <Power className="h-4 w-4" />
            )
          }
          variant={material.active ? 'danger' : 'success'}
          size="sm"
          onClick={() => onToggleActive(material)}
          className="min-h-[38px]"
        />
      </div>
    </article>
  );
};

export default RawMaterialCard;